import { ApiFile, ApiFileTypes, ApiSectionGroup, SPEC_DOCUMENT_TYPES } from "../shared/interfaces";

export function hasRenderableApiFile(file: ApiSectionGroup["file"]): file is ApiFile {
    return !!file?.id && typeof file.fileName === "string" && typeof file.fileUrl === "string";
}

export function resolveRenderableApiFile(
    file: ApiFile,
    sectionGroup?: ApiSectionGroup | null,
): ApiFile | null {
    if (file.fileType !== ApiFileTypes.Drawing) {
        return file;
    }

    const sectionGroupFile = sectionGroup?.file;
    return hasRenderableApiFile(sectionGroupFile) ? sectionGroupFile : null;
}

export function sectionGroupForRoute(
    routeSectionGroupId: string | undefined,
    navigationSectionGroup: ApiSectionGroup | undefined,
    fetchedSectionGroup: ApiSectionGroup | null | undefined,
): ApiSectionGroup | null {
    if (!routeSectionGroupId) {
        return fetchedSectionGroup ?? null;
    }

    if (navigationSectionGroup?.id !== routeSectionGroupId) {
        return fetchedSectionGroup ?? null;
    }

    // A lagging fetch can still hold the PREVIOUS sheet (different id). Merging
    // it would graft the wrong sheet's file onto this route's section group —
    // showing one sheet's artwork (and stale textbox links) under another's
    // identity. Only merge when the fetched group is actually this route's group.
    if (!fetchedSectionGroup || fetchedSectionGroup.id !== routeSectionGroupId) {
        return navigationSectionGroup;
    }

    const navigationFile = navigationSectionGroup.file;
    const fetchedFile = fetchedSectionGroup.file;

    return {
        ...fetchedSectionGroup,
        ...navigationSectionGroup,
        file: hasRenderableApiFile(navigationFile)
            ? navigationFile
            : (fetchedFile ?? navigationFile),
        revision: navigationSectionGroup.revision ?? fetchedSectionGroup.revision,
    };
}

/**
 * Returns the first section group that looks renderable (has a file id),
 * falling back to the first group if none have files.
 */
export function firstRenderableSectionGroup(
    groups: ApiSectionGroup[] | undefined | null,
): ApiSectionGroup | null {
    if (!groups || groups.length === 0) return null;
    return groups.find((g) => !!g.file?.id) ?? groups[0] ?? null;
}

export function sectionGroupAtOrBeforePage(
    groups: ApiSectionGroup[] | undefined | null,
    page: number,
): ApiSectionGroup | null {
    if (!groups || groups.length === 0) return null;
    return groups.reduce<ApiSectionGroup | null>((best, group) => {
        const start = group.start_page_in_file;
        if (start === null || start > page) return best;
        if (!best || start > (best.start_page_in_file ?? Number.NEGATIVE_INFINITY)) {
            return group;
        }
        return best;
    }, null);
}

export function sectionGroupForFilePage(
    fileId: string,
    page: number | undefined,
    sectionGroups: ApiSectionGroup[] | undefined | null,
): ApiSectionGroup | null {
    if (!sectionGroups) return null;

    const fileGroups = sectionGroups.filter((group) => group.file?.id === fileId);
    if (page === undefined) {
        return fileGroups.length === 1 ? fileGroups[0] : null;
    }

    return (
        sectionGroupAtOrBeforePage(fileGroups, page) ??
        sectionGroupAtOrBeforePage(
            sectionGroups.filter((group) => group.document_id === fileId),
            page,
        )
    );
}

export function sectionGroupForPageMap(
    map: Record<string, ApiSectionGroup> | undefined,
    page: number,
): ApiSectionGroup | null {
    const groups = map ? Object.values(map) : [];
    if (groups.length === 1) return groups[0];
    return sectionGroupAtOrBeforePage(groups, page);
}

/**
 * A section whose file is a book of sections rather than its own PDF, so a download of
 * the file is more than the section. Either the file holds other sections
 * (`file_section_count`), or the section starts past the first page (which covers payloads
 * without the count).
 * Callers also require a spec document; the rule says nothing about sheets.
 */
export function isSpecSectionInBook(
    sg: Pick<ApiSectionGroup, "file_section_count" | "start_page_in_file">,
): boolean {
    return (sg.file_section_count ?? 0) > 1 || (sg.start_page_in_file ?? 0) > 0;
}

/**
 * The viewed section when its download should offer the section's own pages beside the
 * book: a spec document's section that lives in a book. Null for own-file sections and
 * for every other document type (submittals, contracts and codes keep one Download).
 */
export function specBookSectionFor(
    sectionGroup: ApiSectionGroup | null | undefined,
    documentType: string | null | undefined,
): ApiSectionGroup | null {
    return sectionGroup &&
        SPEC_DOCUMENT_TYPES.has(documentType ?? "") &&
        isSpecSectionInBook(sectionGroup)
        ? sectionGroup
        : null;
}

/**
 * The section group a viewer's element reload is scoped to. Every sheet view
 * names its revision-specific group: without it, reloads exclude inactive
 * contract sheets and can mix components from other revisions/packages
 * sharing the PDF. Specs stay file/page scoped because boundary pages carry
 * sections from neighboring chapters.
 */
export function elementsScopeSectionGroupIdFor(
    isSheet: boolean,
    sectionGroup: ApiSectionGroup | null | undefined,
): string | undefined {
    return isSheet ? sectionGroup?.id : undefined;
}
